import { useEffect, useState } from "react";

const DEFAULT_INITIAL = 6;
const DEFAULT_BATCH = 6;
const DEFAULT_TIMEOUT_MS = 150;

/**
 * Reveals list items in idle-time batches so the initial paint does not mount
 * every heavy card component in one long main-thread task (reduces TBT).
 */
export function useProgressiveRenderCount(
  totalCount: number,
  resetKey: string | number,
  options?: {
    initial?: number;
    batchSize?: number;
    timeoutMs?: number;
    waitForLoad?: boolean;
  },
) {
  const initial = options?.initial ?? DEFAULT_INITIAL;
  const batchSize = options?.batchSize ?? DEFAULT_BATCH;
  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const waitForLoad = options?.waitForLoad ?? false;

  const [visibleCount, setVisibleCount] = useState(() =>
    Math.min(initial, totalCount),
  );

  useEffect(() => {
    setVisibleCount(Math.min(initial, totalCount));
  }, [resetKey, totalCount, initial]);

  useEffect(() => {
    if (visibleCount >= totalCount) return;

    let cancelled = false;
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const revealMore = () => {
      if (cancelled) return;
      setVisibleCount((prev) => Math.min(prev + batchSize, totalCount));
    };

    const schedule = () => {
      if (cancelled) return;
      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(revealMore, { timeout: timeoutMs });
        return;
      }
      timeoutId = window.setTimeout(revealMore, timeoutMs);
    };

    if (waitForLoad && document.readyState !== "complete") {
      window.addEventListener("load", schedule, { once: true });
      return () => {
        cancelled = true;
        window.removeEventListener("load", schedule);
        if (idleId != null) window.cancelIdleCallback(idleId);
        if (timeoutId != null) window.clearTimeout(timeoutId);
      };
    }

    schedule();
    return () => {
      cancelled = true;
      if (idleId != null) window.cancelIdleCallback(idleId);
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [visibleCount, totalCount, batchSize, timeoutMs, waitForLoad]);

  return visibleCount;
}
